import React, { useMemo, useState } from "react";
import { Grid, Card, Typography, Divider, Box, Chip } from "@mui/material";
import MDBox from "components/MDBox";
import { LocalHospital, ContentCut, BedroomParent, LocalPharmacy } from "@mui/icons-material";
import GaharJobCard from "components/GaharJobCard";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import GaharJobEditDialog from "components/GaharJobEditDialog";
import { updateGaharJob } from "services/api";

// Configuration for Categories
const CATEGORY_CONFIG = {
  CRITICAL: {
    title: "Critical Care",
    arTitle: "العناية الحرجة",
    color: "#e74c3c", // Red
    icon: <LocalHospital />,
  },
  SURGICAL: {
    title: "Surgical / Procedural",
    arTitle: "الجراحية /  الأجرائية العالية",
    color: "#3498db", // Blue
    icon: <ContentCut />,
  },
  INTERNAL: {
    title: "Internal / Clinical",
    arTitle: "الباطنية / السريرية",
    color: "#2ecc71", // Green
    icon: <BedroomParent />,
  },
  SPECIALIZED: {
    title: "Specialized Clinics",
    arTitle: "العيادات التخصصية",
    color: "#9b59b6", // Purple
    icon: <LocalPharmacy />,
  },
};

const GaharPhase1Page = ({ jobs, scenario, setJobs }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const { t, i18n } = useTranslation();

  // HANDLER: Open Dialog
  const handleEditClick = (job) => {
    setSelectedJob(job);
    setOpenEditDialog(true);
  };

  // HANDLER: Save Changes
  const handleSaveChanges = async (updatedJobData) => {
    // 1. Call API
    // Note: Ensure your backend accepts 'workloadVolume' update
    const res = await updateGaharJob(updatedJobData);

    if (res !== "error") {
      // 2. Update Local State (Optimistic UI update or use result)
      setJobs((prevJobs) => prevJobs.map((j) => (j.id === res.id ? { ...j, ...res } : j)));
    }
  };

  // 1. Group Jobs by Category
  const groupedJobs = useMemo(() => {
    const groups = {
      CRITICAL: [],
      SURGICAL: [],
      INTERNAL: [],
      SPECIALIZED: [],
    };
    jobs?.forEach((job) => {
      // Handle null or case differences
      const cat = job.category ? job.category.toUpperCase() : "SPECIALIZED";
      if (groups[cat]) {
        groups[cat].push(job);
      } else {
        // Fallback for unknown categories
        groups["SPECIALIZED"].push(job);
      }
    });

    return groups;
  }, [jobs]);

  return (
    <MDBox py={3}>
      {/* ✅ SCENARIO TITLE HEADER */}
      <MDBox mb={3} display="flex" flexDirection="column">
        <Typography variant="h4" fontWeight="bold" color="dark">
          {scenario?.scenarioName || "Scenario Overview"}
        </Typography>

        {/* Optional: Show Scenario Type underneath */}
        <Box display="flex" gap={1} alignItems="center" mt={0.5}>
          <Typography variant="button" fontWeight="bold" color="text" sx={{ opacity: 0.7 }}>
            {t("user.training.type")}:
          </Typography>
          <Typography variant="button" fontWeight="regular" color="text">
            {scenario?.type}
          </Typography>
        </Box>
      </MDBox>
      <Grid container spacing={3}>
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
          <Grid item xs={12} md={6} xl={3} key={key}>
            <Card
              sx={{
                height: "100%",
                backgroundColor: "#f8f9fa",
                borderTop: `4px solid ${config.color}`,
                overflow: "visible",
              }}
            >
              {/* Header */}
              <MDBox p={2} display="flex" alignItems="center" gap={1}>
                <MDBox
                  bgColor="white"
                  color="dark"
                  width="2.5rem"
                  height="2.5rem"
                  borderRadius="lg"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  shadow="md"
                  sx={{ color: config.color }}
                >
                  {config.icon}
                </MDBox>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="text">
                    {i18n.language === "ar" ? config.arTitle : config.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {i18n.language === "ar" ? config.title : config.arTitle}
                  </Typography>
                </Box>
              </MDBox>

              <MDBox p={2} pt={0}>
                {groupedJobs[key].length === 0 ? (
                  <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
                    {t("admin.compToJob.noJobsFound")}
                  </Typography>
                ) : (
                  groupedJobs[key].map((job) => (
                    <GaharJobCard
                      key={job.id}
                      job={job}
                      onEdit={() => handleEditClick(job)}
                      setJobs={setJobs}
                    />
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>
      {selectedJob && (
        <GaharJobEditDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          job={selectedJob}
          onSave={handleSaveChanges}
        />
      )}
    </MDBox>
  );
};

GaharPhase1Page.propTypes = {
  jobs: PropTypes.array.isRequired,
  scenario: PropTypes.object.isRequired,
  setJobs: PropTypes.func.isRequired,
};

export default GaharPhase1Page;
